/** `/login` — email + password → tokens. */
import { useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { FormField } from '@/components/ui/FormField'
import { useLogin } from '@/hooks/useAuthApi'
import { useAuthStore } from '@/stores/auth'

interface FormValues { email: string; password: string; rememberMe: boolean }

export default function Login() {
  const { t } = useTranslation(['auth', 'common'])
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, user } = useAuthStore.getState()
  const login = useLogin()

  useEffect(() => {
    if (isAuthenticated()) {
      navigate(user?.is_active ? '/app' : '/pending', { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    defaultValues: { rememberMe: true },
  })

  async function onSubmit(values: FormValues) {
    try {
      const data = await login.mutateAsync(values)
      const dest = data.user.is_active ? '/app' : '/pending'
      const from = (location.state as { from?: { pathname: string } })?.from?.pathname
      navigate(from && data.user.is_active ? from : dest, { replace: true })
    } catch { /* error shown below */ }
  }

  /**
   * Map server error text (English-only on the API side) to a localised
   * string when we recognise the canonical detail. Unknown details are
   * surfaced as-is — keeps debug paths intact.
   */
  function localisedApiError(detail: unknown): string {
    if (typeof detail !== 'string') return t('auth:errors.login_failed')
    if (detail.toLowerCase().includes('invalid email or password')) {
      return t('auth:errors.invalid_credentials')
    }
    return detail
  }

  const apiError =
    login.isError && isAxiosError(login.error)
      ? localisedApiError(login.error.response?.data?.detail)
      : null

  return (
    <AuthLayout
      title={t('auth:login.title')}
      subtitle={t('auth:login.subtitle')}
      footer={
        <>
          {t('auth:login.no_account')}{' '}
          <Link className="text-fire hover:underline" to="/register">
            {t('auth:login.create_one')}
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          label={t('auth:login.email_label')}
          htmlFor="email" error={errors.email?.message}
        >
          <Input
            id="email"
            type="email"
            autoFocus
            autoComplete="email"
            placeholder={t('auth:login.email_placeholder')}
            invalid={!!errors.email}
            {...register('email', {
              required: t('auth:validation.email_required'),
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: t('auth:validation.email_invalid'),
              },
            })}
          />
        </FormField>

        <FormField
          label={t('auth:login.password_label')}
          htmlFor="password" error={errors.password?.message}
        >
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            invalid={!!errors.password}
            {...register('password', {
              required: t('auth:validation.password_required'),
              minLength: { value: 8, message: t('auth:validation.password_min') },
            })}
          />
        </FormField>

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm text-textDim cursor-pointer select-none">
            <input
              type="checkbox"
              className="accent-fire w-4 h-4"
              {...register('rememberMe')}
            />
            {t('auth:login.remember_me')}
          </label>
          <Link className="text-sm text-textDim hover:text-fire" to="/forgot-password">
            {t('auth:login.forgot')}
          </Link>
        </div>

        {apiError && (
          <div className="bg-rose-faint text-rose text-sm rounded px-3 py-2">
            {apiError}
          </div>
        )}

        <Button type="submit" className="w-full" loading={login.isPending}>
          {t('auth:login.submit')}
        </Button>
      </form>
    </AuthLayout>
  )
}
